import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, ExternalLink, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/contexts/useAuth';
import type { GoogleLoginResult } from '@/contexts/auth-context';
import { useToast } from '@/hooks/use-toast';
import { trackEvent } from '@/lib/analytics';
import {
  androidChromeIntentUrl,
  detectInAppBrowser,
  isGoogleSignInReady,
  prepareGoogleSignIn,
  type InAppBrowser,
} from '@/lib/google-signin';
import { cn } from '@/lib/utils';

export interface GoogleSignInButtonProps {
  /** 'full': the white "Continue with Google" bar. 'icon': a 40px round G (the navbar). */
  variant?: 'full' | 'icon';
  /** Where the button sits, for analytics (google_signin_clicked). */
  source: string;
  className?: string;
  /**
   * Runs right after the popup has opened inside the tap (the navbar closes
   * its sheet here). Only when the SDK was already loaded: a tap that has to
   * wait for it may get its popup blocked, and the toast then asks for a
   * second tap on this same button, so it must still be on screen. Use
   * onSignedIn to tidy up after that kind of tap.
   */
  onBeforePopup?: () => void;
  onSignedIn?: (result: { isNewUser: boolean }) => void;
}

// A failed sign-in's code (AuthContext.loginWithGoogle) as the toast's text.
// 'cancelled' never gets here: closing Google's window is not an error.
const ERROR_MESSAGE_KEYS: Record<string, string> = {
  popup_blocked: 'auth.google.popupBlocked',
  tap_again: 'auth.google.tapAgain',
  unauthorized_domain: 'auth.google.unauthorizedDomain',
  network: 'auth.google.network',
  unavailable: 'auth.google.unavailable',
  google_signin_disabled: 'auth.google.unavailable',
  google_email_unverified: 'auth.google.emailUnverified',
  account_merged: 'auth.google.accountMerged',
  account_exists: 'auth.google.accountExists',
};

// Google's white button (its brand guidelines): the four-colour G on white,
// dark text, a hairline border. Size and corner radius follow the control it
// sits beside, through className.
const FULL_CLASSES =
  'inline-flex min-h-[44px] w-full items-center justify-center gap-3 rounded-xl border border-[#dadce0] bg-white px-4 py-2 text-sm font-semibold text-[#1f1f1f] shadow-[0_1px_2px_rgba(0,0,0,0.25)] transition-[background-color,box-shadow,transform] duration-200 hover:bg-[#f7f8f8] hover:shadow-[0_1px_3px_rgba(0,0,0,0.3)] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-wait disabled:opacity-80';
const ICON_CLASSES =
  'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#dadce0] bg-white shadow-[0_1px_2px_rgba(0,0,0,0.25)] transition-[background-color,border-color,transform] duration-300 hover:bg-[#f7f8f8] active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-wait';

function GoogleLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" width="18" height="18" aria-hidden="true" focusable="false" className={cn('shrink-0', className)}>
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

type IdleWindow = Window & {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  cancelIdleCallback?: (id: number) => void;
};

/** Runs fn once the page is idle (a short timer where requestIdleCallback is missing: Safari). */
function whenIdle(fn: () => void): () => void {
  const w = window as IdleWindow;
  if (typeof w.requestIdleCallback === 'function') {
    const id = w.requestIdleCallback(fn, { timeout: 2000 });
    return () => w.cancelIdleCallback?.(id);
  }
  const id = window.setTimeout(fn, 1);
  return () => window.clearTimeout(id);
}

function warmUp() {
  // A failed warm-up is retried by the next hover, focus or tap.
  prepareGoogleSignIn().catch(() => undefined);
}

/**
 * "Continue with Google" (2026-09-25). The Firebase SDK is loaded ahead of
 * the tap (once the button is on screen, or on hover / focus) so the popup
 * can open inside the tap; see lib/google-signin.ts for why that matters.
 * In an in-app browser (Telegram, Instagram, TikTok...) Google refuses to
 * sign anyone in, so the tap explains how to get to a real browser instead.
 */
export function GoogleSignInButton({ variant = 'full', source, className, onBeforePopup, onSignedIn }: GoogleSignInButtonProps) {
  const { t } = useTranslation();
  const { loginWithGoogle } = useAuth();
  const { toast } = useToast();
  const [pending, setPending] = useState(false);
  const [inApp, setInApp] = useState<InAppBrowser | null>(null);
  const [copyFailed, setCopyFailed] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const mountedRef = useRef(true);
  // Tests (and any caller) may provide a partial auth context: no Google then.
  const available = typeof loginWithGoogle === 'function';

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Warm up once the button is actually on screen (a display:none navbar
  // icon on a phone never is), when the page is idle.
  useEffect(() => {
    if (!available || detectInAppBrowser()) return undefined;
    let cancelIdle: (() => void) | null = null;
    const start = () => {
      if (!cancelIdle) cancelIdle = whenIdle(warmUp);
    };
    const el = buttonRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      start();
      return () => cancelIdle?.();
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        observer.disconnect();
        start();
      }
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      cancelIdle?.();
    };
  }, [available]);

  const showResult = (result: GoogleLoginResult) => {
    if (result.success) {
      toast({ title: t(result.isNewUser ? 'auth.google.welcomeNewTitle' : 'auth.google.welcomeBackTitle') });
      onSignedIn?.({ isNewUser: result.isNewUser === true });
      return;
    }
    if (result.code === 'cancelled') return;
    toast({
      title: t('auth.google.failedTitle'),
      description: t(ERROR_MESSAGE_KEYS[String(result.code)] || 'auth.google.generic'),
      variant: 'destructive',
    });
  };

  const handleClick = () => {
    if (!available || pending) return;
    const inAppBrowser = detectInAppBrowser();
    trackEvent('google_signin_clicked', { source, inAppBrowser: inAppBrowser?.app ?? null });
    if (inAppBrowser) {
      setCopyFailed(false);
      setInApp(inAppBrowser);
      return;
    }
    // loginWithGoogle opens the popup in its first statement, so it runs
    // before anything else here: no state update and no closing menu first.
    // Only a ready SDK opens it in this tap (2026-09-25 review): a phone's
    // navbar sheet is often tapped before its warm-up is done, and closing
    // the sheet then would leave the "tap again" toast pointing at a button
    // that is gone. So the caller's onBeforePopup waits for a ready SDK.
    const popupInTap = isGoogleSignInReady();
    const attempt = loginWithGoogle();
    if (popupInTap) onBeforePopup?.();
    setPending(true);
    attempt
      .then(showResult, () => showResult({ success: false, code: 'unknown' }))
      .finally(() => {
        // The navbar sheet unmounts this button while Google's window is up;
        // the toast above still shows, the spinner has nowhere to go.
        if (mountedRef.current) setPending(false);
      });
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast({ title: t('auth.google.linkCopied') });
    } catch {
      // In-app browsers often refuse the clipboard: show the link to copy by hand.
      setCopyFailed(true);
    }
  };

  if (!available) return null;

  const label = t('auth.continueWithGoogle');
  const isIcon = variant === 'icon';

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={handleClick}
        onPointerEnter={warmUp}
        onFocus={warmUp}
        disabled={pending}
        aria-busy={pending || undefined}
        aria-label={isIcon ? label : undefined}
        title={isIcon ? label : undefined}
        data-testid="google-signin"
        data-source={source}
        translate="no"
        className={cn(isIcon ? ICON_CLASSES : FULL_CLASSES, className)}
      >
        {pending ? (
          <Loader2 className="h-[18px] w-[18px] shrink-0 animate-spin text-[#5f6368]" aria-hidden="true" />
        ) : (
          <GoogleLogo />
        )}
        {!isIcon && <span className="truncate">{label}</span>}
      </button>

      {inApp && (
        <Dialog open onOpenChange={(open) => { if (!open) setInApp(null); }}>
          <DialogContent data-testid="google-signin-in-app" className="max-w-[calc(100vw-2rem)] rounded-2xl sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>{t('auth.google.inAppTitle')}</DialogTitle>
              <DialogDescription>{t('auth.google.inAppBody')}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              {inApp.platform === 'android' && (
                <a
                  href={androidChromeIntentUrl(window.location.href)}
                  className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
                >
                  <ExternalLink className="h-4 w-4" aria-hidden="true" />
                  {t('auth.google.inAppAndroidCta')}
                </a>
              )}
              {inApp.platform === 'ios' && (
                <p className="rounded-xl border border-border bg-muted/40 px-4 py-3 text-sm text-foreground">
                  {t('auth.google.inAppIosHint')}
                </p>
              )}
              <button
                type="button"
                onClick={copyLink}
                className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl border border-border px-4 text-sm font-semibold text-foreground transition-colors hover:bg-muted/40"
              >
                <Copy className="h-4 w-4" aria-hidden="true" />
                {t('auth.google.copyLink')}
              </button>
              {copyFailed && (
                <input
                  readOnly
                  autoFocus
                  value={window.location.href}
                  aria-label={t('auth.google.copyLink')}
                  onFocus={(e) => e.currentTarget.select()}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-xs text-foreground"
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

export default GoogleSignInButton;
